import React, { useEffect, useMemo, useRef, useState } from "react";
import { getEntities, getTaskReport, SERVER_API } from "../../API";
import TaskReportSummary from "./TaskReportSummary";
import { ColumnFilter } from "../../components/ColumnFilter";

function normalizeList(response) {
  if (Array.isArray(response)) return response;
  if (response?._embedded) return Object.values(response._embedded).flat();
  if (response?.content) return response.content;
  return [];
}

function formatDate(epoch) {
  if (!epoch) return "";
  return new Date(epoch).toLocaleDateString("en-CA");
}

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: CURRENT_YEAR - 2022 }, (_, i) => String(CURRENT_YEAR + 1 - i));
const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];

function uniqueValues(arr) {
  return Array.from(new Set(arr.filter((v) => v !== null && v !== undefined && v !== ""))).sort();
}

function matches(selected, value) {
  return selected.length === 0 || selected.includes(value);
}

function matchesFilter(epoch, year, month) {
  if (!epoch || !year) return true;
  const d = new Date(epoch);
  if (d.getFullYear() !== parseInt(year)) return false;
  if (month && (d.getMonth() + 1) !== parseInt(month)) return false;
  return true;
}

const SKIP_REASON_LABELS = {
  STORE_CLOSED: "Store closed",
  NOT_ON_SHELF: "Product not on shelf / delisted",
  NO_ACCESS: "No access / refused entry",
  OUT_OF_TIME: "Out of time",
  OTHER: "Other",
};

function formatSkipReason(reason) {
  return reason ? (SKIP_REASON_LABELS[reason] ?? reason) : "";
}

function formatDuration(ms) {
  if (!ms) return "";
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

export default function TaskReport() {
  const [manufacturers, setManufacturers] = useState([]);
  const [manufacturerId, setManufacturerId] = useState("");
  const [startYear, setStartYear] = useState("");
  const [startMonth, setStartMonth] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [tasks, setTasks] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searching, setSearching] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedPhoto, setExpandedPhoto] = useState(null);
  const [activeTab, setActiveTab] = useState("data");

  const [filterRep, setFilterRep] = useState([]);
  const [filterCity, setFilterCity] = useState([]);
  const [filterProvince, setFilterProvince] = useState([]);
  const [filterAnswers, setFilterAnswers] = useState({});

  // Reset column filters when a new report is loaded
  useEffect(() => {
    setFilterRep([]);
    setFilterCity([]);
    setFilterProvince([]);
    setFilterAnswers({});
  }, [report]);

  const repOptions      = useMemo(() => uniqueValues((report?.rows ?? []).map((r) => r.representativeName)), [report]);
  const cityOptions     = useMemo(() => uniqueValues((report?.rows ?? []).map((r) => r.storeCity)), [report]);
  const provinceOptions = useMemo(() => uniqueValues((report?.rows ?? []).map((r) => r.storeProvince)), [report]);
  const answerOptions   = useMemo(() => {
    if (!report) return {};
    const opts = {};
    (report.questions ?? []).forEach((q) => {
      if (q.type === "RADIO" || q.type === "CHECK") {
        opts[q.no] = uniqueValues((report.rows ?? []).map((r) => r.answers?.[String(q.no)]?.value ?? ""));
      }
    });
    return opts;
  }, [report]);

  const filteredRows = useMemo(() => {
    if (!report) return [];
    return report.rows.filter((row) => {
      if (!matches(filterRep,      row.representativeName)) return false;
      if (!matches(filterCity,     row.storeCity))          return false;
      if (!matches(filterProvince, row.storeProvince))      return false;
      for (const [qNoStr, selected] of Object.entries(filterAnswers)) {
        if (!selected || selected.length === 0) continue;
        const val = row.answers?.[qNoStr]?.value ?? "";
        if (!selected.includes(val)) return false;
      }
      return true;
    });
  }, [report, filterRep, filterCity, filterProvince, filterAnswers]);

  const setFilterAnswer = (qNo, selected) =>
    setFilterAnswers((prev) => ({ ...prev, [qNo]: selected }));

  const comboRef = useRef(null);
  const searchTimer = useRef(null);

  useEffect(() => {
    getEntities("manufacturers", "?search=status:ACTIVE&size=1000").then((r) =>
      setManufacturers(normalizeList(r))
    );
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e) {
      if (comboRef.current && !comboRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Debounced search
  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      const parts = [];
      if (inputValue.trim() && !selectedTask) {
        inputValue.trim().split(/\s+/).forEach(word => parts.push(`name:${word}`));
      }
      if (manufacturerId) parts.push(`manufacturerId:${manufacturerId}`);
      const searchParam = parts.length > 0 ? `?search=${encodeURIComponent(parts.join(","))}` : "";
      setSearching(true);
      getEntities("tasks", searchParam)
        .then((r) => {
          const list = normalizeList(r);
          list.sort((a, b) => (b.startDate ?? 0) - (a.startDate ?? 0));
          setTasks(list.filter((t) => matchesFilter(t.startDate, startYear, startMonth)));
        })
        .finally(() => setSearching(false));
    }, 400);
    return () => clearTimeout(searchTimer.current);
  }, [inputValue, manufacturerId, startYear, startMonth, selectedTask]);

  const selectTask = async (task) => {
    setSelectedTask(task);
    setInputValue(task.name);
    setShowDropdown(false);
    setReport(null);
    setError(null);
    setLoading(true);
    setActiveTab("data");
    try {
      setReport(await getTaskReport(task.id));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    setSelectedTask(null);
    setReport(null);
    setShowDropdown(true);
  };

  const handleFilterChange = (setter) => (e) => {
    setter(e.target.value);
    setSelectedTask(null);
    setInputValue("");
    setReport(null);
    setShowDropdown(true);
  };

  return (
    <div className="flex-grow-1 overflow-auto">
      <div className="container-fluid py-4 px-4">

        {/* Filters */}
        <div className="card shadow-sm mb-4">
          <div className="card-body py-3">
            <div className="row g-2 align-items-end">

              {/* Task combobox */}
              <div className="col-md-4" ref={comboRef} style={{ position: "relative" }}>
                <label className="form-label small fw-semibold mb-1">Task</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search tasks…"
                  value={inputValue}
                  onChange={handleInputChange}
                  onFocus={() => setShowDropdown(true)}
                  autoComplete="off"
                />
                {showDropdown && (
                  <div
                    className="border rounded bg-white shadow"
                    style={{
                      position: "absolute",
                      top: "calc(100% + 2px)",
                      left: 0,
                      right: 0,
                      zIndex: 1050,
                      maxHeight: 300,
                      overflowY: "auto",
                    }}
                  >
                    {searching ? (
                      <div className="px-3 py-2 text-muted small">Searching…</div>
                    ) : tasks.length === 0 ? (
                      <div className="px-3 py-2 text-muted small">No tasks found</div>
                    ) : (
                      tasks.map((t) => (
                        <div
                          key={t.id}
                          className="px-3 py-2 border-bottom"
                          style={{ cursor: "pointer" }}
                          onMouseEnter={(e) => e.currentTarget.classList.add("bg-light")}
                          onMouseLeave={(e) => e.currentTarget.classList.remove("bg-light")}
                          onMouseDown={() => selectTask(t)}
                        >
                          <div style={{ fontSize: "0.875rem" }}>{t.name}</div>
                          <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                            {formatDate(t.startDate)}
                            {t.endDate ? ` – ${formatDate(t.endDate)}` : ""}
                            {t.status === "INACTIVE" && " · Inactive"}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* Manufacturer */}
              <div className="col-md-4">
                <label className="form-label small fw-semibold mb-1">Manufacturer</label>
                <select
                  className="form-select"
                  value={manufacturerId}
                  onChange={handleFilterChange(setManufacturerId)}
                >
                  <option value="">All manufacturers</option>
                  {manufacturers.map((m) => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </select>
              </div>

              {/* Year */}
              <div className="col-md-2">
                <label className="form-label small fw-semibold mb-1">Year</label>
                <select
                  className="form-select"
                  value={startYear}
                  onChange={(e) => {
                    setStartYear(e.target.value);
                    setStartMonth("");
                    setSelectedTask(null);
                    setInputValue("");
                    setReport(null);
                    setShowDropdown(true);
                  }}
                >
                  <option value="">All years</option>
                  {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>

              {/* Month */}
              <div className="col-md-2">
                <label className="form-label small fw-semibold mb-1">Month</label>
                <select
                  className="form-select"
                  value={startMonth}
                  onChange={handleFilterChange(setStartMonth)}
                  disabled={!startYear}
                >
                  <option value="">All months</option>
                  {MONTHS.map((name, i) => (
                    <option key={i + 1} value={String(i + 1)}>{name}</option>
                  ))}
                </select>
              </div>

            </div>
          </div>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        {loading && (
          <div className="d-flex justify-content-center py-5">
            <div className="spinner-border text-secondary" role="status" />
          </div>
        )}

        {/* Report */}
        {report && (
          <>
            <div className="d-flex align-items-center justify-content-between mb-2">
              <div>
                <h6 className="mb-0">{report.taskName}</h6>
                <div className="text-muted small">
                  {filteredRows.length !== report.rows.length
                    ? `${filteredRows.length} of ${report.rows.length} visits`
                    : `${report.rows.length} visit${report.rows.length !== 1 ? "s" : ""}`}
                </div>
              </div>
              <div className="d-flex align-items-center gap-3">
                <a
                  href={`${SERVER_API}/reports/tasks/${selectedTask.id}/export`}
                  className="btn btn-sm btn-outline-success"
                >
                  Export Excel
                </a>
                <ul className="nav nav-tabs border-bottom-0 mb-0">
                <li className="nav-item">
                  <button
                    className={`nav-link${activeTab === "data" ? " active" : ""}`}
                    onClick={() => setActiveTab("data")}
                  >Responses</button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link${activeTab === "summary" ? " active" : ""}`}
                    onClick={() => setActiveTab("summary")}
                  >Summary</button>
                </li>
              </ul>
              </div>
            </div>

            {activeTab === "summary" && <TaskReportSummary report={report} />}

            {activeTab === "data" && <div className="table-responsive">
              <table
                className="table table-hover table-bordered mb-0"
                style={{ fontSize: "0.85rem" }}
              >
                <thead className="table-light">
                  <tr>
                    <th style={{ whiteSpace: "nowrap" }}>Date</th>
                    <ColumnFilter label="Representative" options={repOptions}      value={filterRep}      onChange={setFilterRep}      minWidth={120} />
                    <th style={{ minWidth: 160 }}>Address</th>
                    <ColumnFilter label="City"           options={cityOptions}     value={filterCity}     onChange={setFilterCity} />
                    <ColumnFilter label="Province"       options={provinceOptions} value={filterProvince} onChange={setFilterProvince} />
                    <th style={{ whiteSpace: "nowrap" }}>Time Spent</th>
                    <th style={{ whiteSpace: "nowrap" }}>Status</th>
                    {report.questions.map((q) =>
                      q.type === "RADIO" || q.type === "CHECK" ? (
                        <ColumnFilter
                          key={q.no}
                          label={q.text}
                          options={answerOptions[q.no] ?? []}
                          value={filterAnswers[q.no] ?? []}
                          onChange={(v) => setFilterAnswer(q.no, v)}
                          minWidth={160}
                        />
                      ) : (
                        <th key={q.no} style={{ minWidth: q.type === "PHOTO" ? 170 : 160 }}>{q.text}</th>
                      )
                    )}
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.length === 0 ? (
                    <tr>
                      <td colSpan={7 + report.questions.length} className="text-center text-muted py-4">
                        {report.rows.length === 0 ? "No data found for this task." : "No visits match the current filters."}
                      </td>
                    </tr>
                  ) : (
                    filteredRows.map((row, i) => (
                      <tr key={i}>
                        <td style={{ whiteSpace: "nowrap" }}>{formatDate(row.visitDate)}</td>
                        <td style={{ whiteSpace: "nowrap" }}>{row.representativeName}</td>
                        <td>{row.storeAddress}</td>
                        <td>{row.storeCity}</td>
                        <td>{row.storeProvince}</td>
                        <td style={{ whiteSpace: "nowrap" }}>{formatDuration(row.durationMs)}</td>
                        <td style={{ whiteSpace: "nowrap" }}>
                          {row.status === "SKIPPED" ? (
                            <span>
                              <span className="badge bg-warning text-dark">Skipped</span>
                              <span className="ms-1 text-muted small">{formatSkipReason(row.skipReason)}</span>
                              {row.skipNotes && <div className="text-muted small fst-italic">{row.skipNotes}</div>}
                            </span>
                          ) : (
                            <span className="badge bg-success">Completed</span>
                          )}
                        </td>
                        {row.status === "SKIPPED"
                          ? report.questions.map((q) => <td key={q.no} className="text-muted">—</td>)
                          : report.questions.map((q) => {
                              const ans = row.answers?.[String(q.no)];
                              if (!ans) return <td key={q.no} />;
                              if (ans.type === "PHOTO" && ans.fileUrl) {
                                return (
                                  <td key={q.no}>
                                    <img
                                      src={`${SERVER_API}${ans.fileUrl}`}
                                      alt=""
                                      style={{
                                        width: 160, height: 90, objectFit: "contain",
                                        cursor: "pointer", borderRadius: 4, background: "#f0f0f0",
                                      }}
                                      onClick={() => setExpandedPhoto(`${SERVER_API}${ans.fileUrl}`)}
                                    />
                                  </td>
                                );
                              }
                              return <td key={q.no}>{ans.value ?? ""}</td>;
                            })
                        }
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>}
          </>
        )}

        {expandedPhoto && (
          <div
            className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
            style={{ backgroundColor: "rgba(0,0,0,0.85)", zIndex: 9999 }}
            onClick={() => setExpandedPhoto(null)}
          >
            <img
              src={expandedPhoto}
              alt=""
              style={{ maxHeight: "90vh", maxWidth: "90vw", objectFit: "contain", borderRadius: 6 }}
            />
          </div>
        )}

      </div>
    </div>
  );
}
